import { hashKey, Query, QueryClient, QueryKey } from '@tanstack/react-query';
import { AccountData, TokenResponse } from '../Api';

/** The session decides the scope, so it is the one query that cannot carry one itself. */
export const SESSION_QUERY_KEY = ['login', 'session'] as const;

export const ANONYMOUS_SCOPE = 'anonymous';

/**
 * Data that is the same for every identity: the session (see above) and the translations, which are
 * per language. Scoping the translations would throw them away on every login for nothing -- and
 * the translation query wraps the whole page, so it would suspend the whole page; a per-account
 * entry under that root must therefore never be added, because it would be shared across accounts
 * and survive a logout. It is that one session key, not the whole `login` root: `login.me` and
 * `login.refreshToken` answer per account and are scoped like everything else. Every lookup this
 * client does runs through here, so it compares the two segments instead of hashing the key again.
 */
export const isIdentityIndependent = (queryKey: QueryKey): boolean =>
    (queryKey.length === SESSION_QUERY_KEY.length && queryKey[0] === SESSION_QUERY_KEY[0] && queryKey[1] === SESSION_QUERY_KEY[1]) ||
    queryKey[0] === 'translation';

/**
 * Who is looking at what: the signed-in account and the account it acts as. The main account is
 * part of it because a manager and the owner of the same account see that account with different
 * rights.
 */
export function scopeOf(session: TokenResponse | null | undefined): string {
    const main = session?.isAuthenticated ? session.mainAccount?.accountId : undefined;
    const active = session?.isAuthenticated ? session.activeAccount?.accountId : undefined;
    return main && active ? `${main}:${active}` : ANONYMOUS_SCOPE;
}

export function sessionOf(queryClient: QueryClient): TokenResponse | undefined {
    return queryClient.getQueryData<TokenResponse>(SESSION_QUERY_KEY);
}

export function identityOf(queryClient: QueryClient): { mainAccount: AccountData | null; activeAccount: AccountData | null } {
    const session = sessionOf(queryClient);
    return { mainAccount: session?.mainAccount ?? null, activeAccount: session?.activeAccount ?? null };
}

/** The hash a key gets under a scope; what createQueryClient's queryKeyHashFn produces. */
export const hashKeyInScope = (queryKey: QueryKey, scope: string): string => hashKey(isIdentityIndependent(queryKey) ? queryKey : [...queryKey, scope]);

/**
 * Makes a session the current identity. A refetch of the session that is still under way would
 * answer with the previous identity after this write, so it is cancelled first.
 */
export function writeSession(queryClient: QueryClient, session: TokenResponse): void {
    void queryClient.cancelQueries({ queryKey: SESSION_QUERY_KEY });
    queryClient.setQueryData(SESSION_QUERY_KEY, session);
}

/**
 * Drops everything cached for an identity that is no longer current: its queries (cancelled first,
 * so an answer under way does not land) and the mutation results, which hold that identity's
 * TokenResponses. A late server-rendered result for the old identity still hydrates under its
 * hash afterwards, where nothing looks it up, and is garbage-collected like any unused entry.
 */
export function dropIdentity(queryClient: QueryClient, scope: string): void {
    const ofScope = { predicate: (query: Query) => !isIdentityIndependent(query.queryKey) && query.queryHash === hashKeyInScope(query.queryKey, scope) };
    void queryClient.cancelQueries(ofScope);
    queryClient.removeQueries(ofScope);
    queryClient.getMutationCache().clear();
}
